import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /** Remove loading state from all quick-add "Choose" buttons (e.g. after modal opens or on error). */
  #clearQuickAddLoadingState() {
    this.querySelectorAll('.quick-add__button--choose.loading').forEach((btn) => btn.classList.remove('loading'));
    document.querySelectorAll('.quick-add__button--choose.loading').forEach((btn) => btn.classList.remove('loading'));
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  #captureQuickViewWidgetInfo() {
    const productCard =
      this.closest('product-card') ||
      this.closest('product-card-link') ||
      this.closest('.swiper-slide[data-product-id]') ||
      this.closest('.resource-list__item, .product-grid__item');

    if (!productCard || typeof window.getWidgetInfoFromElement !== 'function') return;

    try {
      const widgetInfo = window.getWidgetInfoFromElement(productCard);
      const productId =
        productCard.getAttribute('data-product-id') ||
        productCard.querySelector?.('[data-product-id]')?.getAttribute('data-product-id') ||
        null;

      if (typeof window.storeQuickViewWidgetInfo === 'function') {
        window.storeQuickViewWidgetInfo(widgetInfo, productId);
      } else {
        window.__QUICK_VIEW_WIDGET_INFO__ = widgetInfo;
        if (productId) {
          window.__PW_QUICK_VIEW_WIDGET_BY_PRODUCT__ = window.__PW_QUICK_VIEW_WIDGET_BY_PRODUCT__ || {};
          window.__PW_QUICK_VIEW_WIDGET_BY_PRODUCT__[String(productId)] = widgetInfo;
        }
      }
    } catch (error) {
      console.error('Error capturing quick-view widget info:', error);
    }
  }

  handleClick = async (event) => {
    // Open Quick Buy only — never submit /cart/add.js from this control.
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    const target = event.target instanceof Element ? event.target : null;
    const button = target ? /** @type {HTMLElement | null} */ (target.closest('.quick-add__button--choose')) : null;
    if (button && !button.classList.contains('loading')) {
      button.classList.add('loading');
    }
    setTimeout(() => {
      this.#captureQuickViewWidgetInfo();
    },0)

    try {
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#openQuickAddModal();
      }
      this.#clearQuickAddLoadingState();
    } catch (err) {
      this.#clearQuickAddLoadingState();
      throw err;
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
    setTimeout(pauseSwipersBehindQuickAdd, 0);
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const productCard = this.closest('product-card');
    const promo =
      window.PWPdpPromo && typeof window.PWPdpPromo.promoFromCard === 'function'
        ? window.PWPdpPromo.promoFromCard(productCard)
        : null;

    await renderQuickAddModalContent(productGrid, {
      productTitle: this.dataset.productTitle || '',
      productUrl: this.productPageUrl,
      selectedVariantId: this.#getSelectedVariantId(),
      promo,
    });
  }

}

/**
 * @typedef {{ productTitle?: string, productUrl?: string, selectedVariantId?: string | null, promo?: { shiksha?: boolean, flashSale?: boolean } | null }} QuickAddRenderOptions
 */

/** @type {AbortController | null} */
let quickAddSwapAbortController = null;

/**
 * Morph product grid HTML into the open Quick Buy modal.
 * @param {Element} productGrid
 * @param {QuickAddRenderOptions} [options]
 */
async function renderQuickAddModalContent(productGrid, options = {}) {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!productGrid || !modalContent) return;

  const productUrl = options.productUrl || '';
  const productTitle =
    options.productTitle ||
    productGrid.querySelector('.view-product-title a')?.textContent?.trim() ||
    '';

  if (isMobileBreakpoint()) {
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');
    const superbundleSelector = productGrid.querySelector('.superbundle-selector');
    const buyContainer = productGrid.querySelector('.buy-buttons-main-container');
    const titleLink = document.createElement('a');
    titleLink.textContent = productTitle;
    titleLink.href = productUrl;

    const productHeader = document.createElement('div');
    productHeader.classList.add('product-header');
    const urgencyLabel = productDetails?.querySelector('.view-product-title .pw-urgency-label');
    if (urgencyLabel) productHeader.appendChild(urgencyLabel);
    productHeader.appendChild(titleLink);
    if (productPrice) productHeader.appendChild(productPrice);
    productGrid.appendChild(productHeader);

    if (variantPicker) productGrid.appendChild(variantPicker);
    if (superbundleSelector) {
      if (variantPicker) variantPicker.appendChild(superbundleSelector);
      else productGrid.appendChild(superbundleSelector);
    }
    if (buyContainer) productGrid.appendChild(buyContainer);
    else if (productFormComponent) productGrid.appendChild(productFormComponent);
    productDetails?.remove();
  }

  morph(modalContent, productGrid);

  const selectedVariantId = options.selectedVariantId;
  if (selectedVariantId) {
    const idInput = modalContent.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    if (idInput instanceof HTMLInputElement) {
      idInput.disabled = false;
      idInput.value = String(selectedVariantId);
    }

    // Prefer native option radios; skip linked-main radios (handled separately)
    const modalInputs = modalContent.querySelectorAll(
      'input[type="radio"][data-variant-id]:not([data-linked-main-variant-input])'
    );
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === String(selectedVariantId) && !input.checked) {
        input.checked = true;
        break;
      }
    }
  }

  if (typeof window.PWLinkedProducts?.bind === 'function') {
    window.PWLinkedProducts.bind();
  }

  if (typeof window.PWSuperbundle?.initIn === 'function') {
    window.PWSuperbundle.initIn(modalContent);
  }

  if (typeof window.PWQuickAddVariantPicker?.mount === 'function') {
    window.PWQuickAddVariantPicker.mount(modalContent);
  }

  pinQuickAddPrice(modalContent);

  if (options.promo) {
    modalContent.setAttribute(
      'data-pw-promo',
      JSON.stringify({
        shiksha: options.promo.shiksha === true,
        flashSale: options.promo.flashSale === true,
      })
    );
  }

  let promo = options.promo || null;
  if (!promo) {
    const rawPromo = modalContent.getAttribute('data-pw-promo');
    if (rawPromo) {
      try {
        promo = JSON.parse(rawPromo);
      } catch {
        promo = null;
      }
    }
  }

  if (window.PWPdpPromo && typeof window.PWPdpPromo.applyWithin === 'function') {
    window.PWPdpPromo.applyWithin(modalContent, promo);
  }
}

/**
 * After morph / superbundle init, keep price directly under the title.
 * Works for mobile `.product-header` and desktop `.view-product-title`.
 * @param {Element | null} root
 */
function pinQuickAddPrice(root) {
  if (!root) return;

  const header = root.querySelector('.product-header');
  const titleBlock = root.querySelector('.view-product-title');

  const price = root.querySelector('product-price');
  const qvPrice = root.querySelector('.superbundle-qv-price');

  if (header) {
    if (price && price.parentElement !== header) header.appendChild(price);
    if (qvPrice) {
      const titleLink = header.querySelector('a');
      if (qvPrice.parentElement !== header || (titleLink && qvPrice.previousElementSibling !== titleLink)) {
        if (titleLink && titleLink.nextSibling) {
          header.insertBefore(qvPrice, titleLink.nextSibling);
        } else {
          header.appendChild(qvPrice);
        }
      }
    }
    return;
  }

  if (titleBlock && titleBlock.parentNode) {
    if (price && price.previousElementSibling !== titleBlock && !qvPrice) {
      titleBlock.parentNode.insertBefore(price, titleBlock.nextSibling);
    }
    if (qvPrice && qvPrice.previousElementSibling !== titleBlock) {
      titleBlock.parentNode.insertBefore(qvPrice, titleBlock.nextSibling);
    }
  }
}

/**
 * Fetch a product page and swap the open Quick Buy modal to that product.
 * Used when selecting a linked-product variant inside the modal.
 * @param {string} productPageUrl
 * @param {{ title?: string, variantId?: string | null }} [options]
 * @returns {Promise<boolean>}
 */
async function swapQuickAddModalProduct(productPageUrl, options = {}) {
  if (!productPageUrl) return false;

  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return false;

  quickAddSwapAbortController?.abort();
  quickAddSwapAbortController = new AbortController();
  modalContent.classList.add('is-loading');

  try {
    const response = await fetch(productPageUrl, { signal: quickAddSwapAbortController.signal });
    if (!response.ok) throw new Error(`Failed to fetch product page: ${response.status}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!gridElement) return false;

    const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
    const title =
      options.title ||
      productGrid.querySelector('.view-product-title a')?.textContent?.trim() ||
      '';

    await renderQuickAddModalContent(productGrid, {
      productTitle: title,
      productUrl: productPageUrl,
      selectedVariantId: options.variantId || null,
    });
    return true;
  } catch (error) {
    if (error?.name === 'AbortError') return false;
    console.error('Failed to swap Quick Buy product', error);
    return false;
  } finally {
    modalContent.classList.remove('is-loading');
    quickAddSwapAbortController = null;
  }
}

window.PWQuickAddModal = {
  render: renderQuickAddModalContent,
  swapProduct: swapQuickAddModalProduct,
};

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/** @type {any[]} */
let pausedQuickAddSwipers = [];

function pauseSwipersBehindQuickAdd() {
  if (pausedQuickAddSwipers.length) return;
  const main = document.getElementById('MainContent');
  if (!main) return;

  const nodes = main.getElementsByClassName('swiper');
  for (let i = 0; i < nodes.length; i++) {
    const swiper = /** @type {any} */ (nodes[i]).swiper;
    if (!swiper || swiper.destroyed) continue;

    pausedQuickAddSwipers.push({
      swiper,
      autoplay: Boolean(swiper.autoplay?.running),
    });
    swiper.autoplay?.stop?.();
    swiper.disable?.();
  }
}

function resumeSwipersBehindQuickAdd() {
  for (let i = 0; i < pausedQuickAddSwipers.length; i++) {
    const item = pausedQuickAddSwipers[i];
    const swiper = item?.swiper;
    if (!swiper || swiper.destroyed) continue;
    swiper.enable?.();
    if (item.autoplay) swiper.autoplay?.start?.();
  }
  pausedQuickAddSwipers = [];
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogOpenEvent.eventName, this.#pausePageBehind);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    if (typeof window.PWLinkedProducts?.bind === 'function') {
      window.PWLinkedProducts.bind();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogOpenEvent.eventName, this.#pausePageBehind);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    resumeSwipersBehindQuickAdd();
  }

  #pausePageBehind = () => {
    if (!this.refs.dialog?.open) return;
    pauseSwipersBehindQuickAdd();
  };

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    const newTitleAnchor = /** @type {HTMLAnchorElement | null} */ (html.querySelector('.view-product-title a'));
    const newShareTitle = html.querySelector('.product-title-share-title');
    const titleText =
      newTitleAnchor?.textContent?.trim() ||
      newShareTitle?.textContent?.trim() ||
      '';
    const titleHref = newTitleAnchor?.getAttribute('href') || '';

    this.querySelectorAll('.view-product-title a, .product-header a, .product-title-share-title').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (titleText) node.textContent = titleText;
      if (node instanceof HTMLAnchorElement && titleHref) node.href = titleHref;
    });
  };

  #handleDialogClose = () => {
    resumeSwipersBehindQuickAdd();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
